import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, TrendingUp, Brain, Shield, Zap, Trophy, BarChart3, Globe, Crown } from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();
  const [showAuth, setShowAuth] = useState(false);
  const [tipster, setTipster] = useState(null);

  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL.replace(/^http:\/\//i, 'https://');
    fetch(`${API}/api/social/tipster-of-the-day`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.tipster) setTipster(d.tipster); })
      .catch(() => {});
  }, []);

  const features = [
    {
      icon: <Brain className="w-8 h-8" />,
      title: "Multi-AI Analyse",
      description: "6 KI-Modelle (GPT-4o, Claude Sonnet, Gemini Flash) analysieren gemeinsam jedes Spiel"
    },
    {
      icon: <Globe className="w-8 h-8" />,
      title: "Alle Sportarten",
      description: "Fußball, Basketball, Tennis, Eishockey, American Football und 100+ weitere"
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: "20+ Faktoren",
      description: "Form, H2H, Verletzungen, Wetter, Schiedsrichter, Motivation und mehr"
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: "Selbstlernend",
      description: "Das System lernt aus jedem Spiel und wird kontinuierlich besser"
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Echtzeit-Quoten",
      description: "Vergleich von 500+ Wettanbietern für die besten Quoten"
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "100% Legal",
      description: "DSGVO-konform, keine Wettberatung, reine Analyse-Software"
    }
  ];

  const pricingTiers = [
    { name: 'Mini', price: 4.99, tips: 5 },
    { name: 'Small', price: 9.99, tips: 10 },
    { name: 'Medium', price: 19.99, tips: 20, featured: true },
    { name: 'Large', price: 49.99, tips: 50 },
    { name: 'XL', price: 99.99, tips: 100 },
    { name: 'XXL', price: 499.99, tips: 500 },
    { name: 'Unlimited', price: 999.99, tips: 'Unbegrenzt' }
  ];

  return (
    <div className="min-h-screen bg-[#121212]">
      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1763854413165-1713bc5a7f4a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHxzdGFkaXVtJTIwY3Jvd2QlMjBuaWdodCUyMGF0bW9zcGhlcmV8ZW58MHx8fHwxNzczODI0MDIzfDA&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-[#121212] via-[#121212]/80 to-transparent"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 text-center">
          <div data-testid="hero-section" className="space-y-8">
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight" style={{ fontFamily: 'Inter, sans-serif' }}>
              Die ultimative <span className="text-[#00BFA6]">KI-gestützte</span><br />
              Sportwetten-Analyse
            </h1>
            
            <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Analyse von 50.000.000+ Datenpunkten durch sechs fuehrende KI-Modelle.
              Alle Sportarten. Alle Ligen. Weltweit.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button 
                data-testid="get-started-btn"
                onClick={() => navigate('/auth')}
                className="bg-[#00BFA6] hover:bg-[#009E8A] text-black font-bold py-6 px-8 rounded-lg text-lg shadow-[0_0_20px_rgba(0,191,166,0.4)] hover:shadow-[0_0_30px_rgba(0,191,166,0.6)] transition-all"
              >
                Jetzt starten <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              
              <Button 
                data-testid="view-pricing-btn"
                onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
                className="border border-white/20 hover:border-white/50 bg-transparent text-white py-6 px-8 rounded-lg text-lg transition-all"
              >
                Preise ansehen
              </Button>
            </div>
            
            {/* Stats */}
            <div className="grid grid-cols-3 gap-8 max-w-2xl mx-auto mt-16">
              <div className="text-center">
                <div className="font-mono text-3xl font-bold text-[#00BFA6]">100+</div>
                <div className="text-sm text-gray-400 mt-1">Sportarten</div>
              </div>
              <div className="text-center">
                <div className="font-mono text-3xl font-bold text-[#00BFA6]">50M+</div>
                <div className="text-sm text-gray-400 mt-1">Datenpunkte</div>
              </div>
              <div className="text-center">
                <div className="font-mono text-3xl font-bold text-[#00BFA6]">6</div>
                <div className="text-sm text-gray-400 mt-1">KI-Modelle</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-[#1E1E1E]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-16">
            Warum <span className="text-[#00BFA6]">OddsCortex</span>?
          </h2>
          
          <div data-testid="features-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                data-testid={`feature-card-${index}`}
                className="bg-[#121212] border border-white/10 rounded-xl p-6 hover:border-[#00BFA6]/50 transition-all duration-300 card-hover"
              >
                <div className="text-[#00BFA6] mb-4">{feature.icon}</div>
                <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tipster des Tages + Simulator CTA */}
      <section className="py-16 bg-[#121212]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Tipster of the Day */}
            {tipster && (
              <div data-testid="landing-tipster-of-day" className="bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border border-yellow-500/20 rounded-2xl p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Crown className="w-8 h-8 text-yellow-400" />
                  <h3 className="text-2xl font-bold text-white">Tipster des Tages</h3>
                </div>
                <div className="space-y-3">
                  <div className="text-3xl font-bold text-yellow-400">{tipster.anon_name}</div>
                  <div className="grid grid-cols-3 gap-4 mt-4">
                    <div>
                      <div className="text-2xl font-bold text-white font-mono">{tipster.tips_count}</div>
                      <div className="text-xs text-gray-400">Tips</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-white font-mono">{tipster.upvotes}</div>
                      <div className="text-xs text-gray-400">Upvotes</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-white font-mono">{tipster.followers}</div>
                      <div className="text-xs text-gray-400">Follower</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Simulator CTA */}
            <div data-testid="landing-simulator-cta" className="bg-gradient-to-br from-[#00BFA6]/10 to-[#7B1FA2]/10 border border-[#00BFA6]/20 rounded-2xl p-8 flex flex-col justify-center">
              <div className="flex items-center gap-3 mb-4">
                <Trophy className="w-8 h-8 text-[#00BFA6]" />
                <h3 className="text-2xl font-bold text-white">Wett-Simulator</h3>
              </div>
              <p className="text-gray-400 mb-6 leading-relaxed">
                Teste deine Strategien risikofrei mit 10.000 virtuellen Coins. Kein echtes Geld, volles Lernpotenzial.
              </p>
              <Button
                data-testid="simulator-cta-btn"
                onClick={() => navigate('/auth')}
                className="bg-[#00BFA6] hover:bg-[#009E8A] text-black font-bold py-3 w-fit"
              >
                Jetzt testen <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-[#121212]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            Wähle deinen <span className="text-[#00BFA6]">Plan</span>
          </h2>
          <p className="text-gray-400 text-center mb-12">Monatlich kündbar. Keine versteckten Kosten.</p>
          
          <div data-testid="pricing-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pricingTiers.map((tier, index) => (
              <div 
                key={index}
                data-testid={`pricing-tier-${tier.name.toLowerCase()}`}
                className={`rounded-xl p-6 ${
                  tier.featured 
                    ? 'bg-gradient-to-b from-[#7B1FA2] to-[#4A148C] border-2 border-[#7B1FA2] neon-glow-purple' 
                    : 'bg-[#1E1E1E] border border-white/10'
                } hover:border-[#00BFA6]/50 transition-all duration-300`}
              >
                {tier.featured && (
                  <div className="text-xs font-bold text-[#00BFA6] mb-2">BELIEBT</div>
                )}
                <h3 className="text-2xl font-bold mb-2">{tier.name}</h3>
                <div className="mb-4">
                  <span className="text-4xl font-bold font-mono">{tier.price}€</span>
                  <span className="text-gray-400 text-sm">/Monat</span>
                </div>
                <div className="text-gray-300 mb-6">
                  <span className="font-bold text-lg">{tier.tips}</span> Tipps/Monat
                </div>
                <Button 
                  onClick={() => navigate('/auth')}
                  className={`w-full py-3 rounded-lg font-bold transition-all ${
                    tier.featured
                      ? 'bg-[#00BFA6] hover:bg-[#009E8A] text-black'
                      : 'bg-white/10 hover:bg-white/20 text-white'
                  }`}
                >
                  Jetzt starten
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Legal Notice */}
      <section className="py-12 bg-[#1E1E1E]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div data-testid="legal-notice" className="legal-notice text-center">
            <p className="text-sm text-gray-300">
              ⚠️ <strong>Wetten können süchtig machen.</strong> Hilfe unter <a href="https://www.bzga.de" className="text-[#00BFA6] underline" target="_blank" rel="noopener noreferrer">www.bzga.de</a><br />
              Nur für Erwachsene ab 18 Jahren. Alle Tipps ohne Gewähr. Keine Wettberatung im rechtlichen Sinne.<br />
              Dies ist eine Analyse-Software, keine Wettvermittlung.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-[#121212] border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center text-gray-400 text-sm">
          <p>© 2026 OddsCortex. Alle Rechte vorbehalten.</p>
          <div className="mt-3 flex flex-wrap justify-center gap-x-6 gap-y-2">
            <a href="/impressum" className="hover:text-[#00BFA6] transition-colors">Impressum</a>
            <a href="/datenschutz" className="hover:text-[#00BFA6] transition-colors">Datenschutz</a>
            <a href="/agb" className="hover:text-[#00BFA6] transition-colors">AGB</a>
            <a href="/faq" className="hover:text-[#00BFA6] transition-colors">FAQ</a>
            <a href="/support" className="hover:text-[#00BFA6] transition-colors">Support</a>
            <a href="https://www.instagram.com/oddscortex?igsh=MWcxdHg2ZnQ4bHptdg==" target="_blank" rel="noopener noreferrer" className="hover:text-[#E1306C] transition-colors">Instagram</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
